import { ApolloLink } from '@apollo/client/index.js';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';

type ErrorValue = {
  [key: string]: unknown;
};

const filtered_params = [
  'password',
  'email',
  'date_of_birth',
  'password_digest',
  'health_care_number',
  'phone_number',
  'results',
  'first_name',
  'last_name',
  'preferred_name',
  'message_content',
  'access_token',
  'one_time_access_token',
];

// handle errors in graphql that don't end up as exceptions
export const errorLink = onError(
  ({ graphQLErrors, networkError, operation }) => {
    //log user out if error is due to invalid token
    if (
      networkError &&
      'statusCode' in networkError &&
      networkError.statusCode === 401
    ) {
      localStorage.removeItem('jwt');
      window.location.reload();
      return; // suppress GQL errors — redirect is already in flight
    }

    const sentryTags = operation.getContext().sentryTags as
      Record<string, string> | undefined;
    const sentryOptions = sentryTags ? { tags: sentryTags } : undefined;

    if (graphQLErrors) {
      const isUnauthorized = graphQLErrors.some(
        (error) => error.extensions?.code === 'unauthorized',
      );
      if (isUnauthorized) {
        localStorage.removeItem('jwt');
        window.location.reload();
        return;
      }

      // not found and forbidden are expected outcomes the UI renders itself
      const reportableErrors = graphQLErrors.filter((error) => {
        const code = error.extensions?.code;
        return code !== 'not_found' && code !== 'forbidden';
      });

      reportableErrors.forEach((error) => {
        const value = error.extensions?.value as ErrorValue;
        if (value && typeof value === 'object') {
          Object.entries(value).forEach(([key]) => {
            if (filtered_params.includes(key)) {
              value[key] = 'sensitive';
            }
          });
        }
      });

      reportableErrors.forEach((reportableError) => {
        Sentry.captureException(
          new Error(`GraphQL Error: ${JSON.stringify(reportableError)}`),
          sentryOptions,
        );
      });
    }

    if (networkError && networkError.name !== 'AbortError') {
      Sentry.captureException(networkError, sentryOptions);
    }
  },
);

export const renewLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const context = operation.getContext();
    const {
      response: { headers },
    } = context;

    if (headers) {
      const refreshToken = headers.get('X-New-Token');

      if (refreshToken) {
        localStorage.setItem('jwt', refreshToken);
      }
    }

    return response;
  });
});
